const PAGE_SCROLL_CONTAINER_SELECTORS = [
  ".client-pages",
  ".client-component",
  ".client-component-fullscreen",
  ".vacation-packages-page",
  ".vacation-packages-container",
  ".vacation-packages-results",
  "#vp-public-main",
  ".vp-public-flow__embedded-search",
  ".vp-public-flow__booking",
  ".vp-public-flow__details",
  ".vp-public-hotel-booking",
  ".pbc",
  ".hotel-profile",
  ".hp__wrapper",
];

const AGENT_SCROLL_CONTAINER_SELECTORS = [
  ".agent-component",
  ".agent-content",
  ".agent-pages",
];

const SCROLL_ANCESTOR_START_SELECTORS = [
  ".vacation-packages-results",
  "#vp-public-main",
  ".vacation-packages-page",
  ".pbc",
  ".vp-public-hotel-booking",
  ".client-component-fullscreen",
  ".client-component",
  ".client-pages",
];

export const PAGE_SCROLL_TOP_THRESHOLD = 280;

function scrollSelectors({ includeAgentShell = true } = {}) {
  if (includeAgentShell) {
    return PAGE_SCROLL_CONTAINER_SELECTORS.concat(AGENT_SCROLL_CONTAINER_SELECTORS);
  }
  return PAGE_SCROLL_CONTAINER_SELECTORS;
}

function isAgentShellNode(element) {
  if (!element || typeof element.closest !== "function") return false;
  return Boolean(
    element.closest(
      ".agent-pages, .agent-component, .agent-content, .hr-admin-layout, .hr-admin-layout-root"
    )
  );
}

function scrollWindowToTop(behavior = "instant") {
  try {
    window.scrollTo({ top: 0, left: 0, behavior });
  } catch (_) {
    window.scrollTo(0, 0);
  }
}

function resetElementScroll(element, behavior = "instant") {
  if (!element || typeof element !== "object") return;
  if (behavior === "smooth" && typeof element.scrollTo === "function") {
    try {
      element.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      return;
    } catch (_) {
      /* fall through */
    }
  }
  element.scrollTop = 0;
  element.scrollLeft = 0;
  if (typeof element.scrollTo === "function") {
    element.scrollTo(0, 0);
  }
}

function resetAncestorScrollports(fromEl, behavior, { includeAgentShell = true } = {}) {
  let node = fromEl;
  while (node && node !== document.documentElement) {
    if (!includeAgentShell && isAgentShellNode(node)) break;
    resetElementScroll(node, behavior);
    node = node.parentElement;
  }
}

function readElementScrollTop(element) {
  return Number(element?.scrollTop) || 0;
}

/**
 * In-page anchors (#vp-home-faq, #pbc-bank, …) must keep their target.
 * Only skip when the hash id is actually in the document.
 */
export function shouldSkipScrollResetForHash(
  hash = typeof window !== "undefined" ? window.location.hash : ""
) {
  const id = String(hash || "").replace(/^#/, "").trim();
  if (!id) return false;
  if (typeof document === "undefined") return false;
  try {
    return Boolean(document.getElementById(id));
  } catch (_) {
    return false;
  }
}

export function getMaxPageScrollTop(root = typeof document !== "undefined" ? document : null) {
  if (typeof window === "undefined") return 0;
  const windowTop =
    Number(window.pageYOffset) ||
    Number(document.documentElement?.scrollTop) ||
    Number(document.body?.scrollTop) ||
    0;
  if (!root || typeof root.querySelectorAll !== "function") return windowTop;
  let max = windowTop;
  scrollSelectors().forEach((selector) => {
    root.querySelectorAll(selector).forEach((element) => {
      const top = readElementScrollTop(element);
      if (top > max) max = top;
    });
  });
  SCROLL_ANCESTOR_START_SELECTORS.forEach((selector) => {
    const start = root.querySelector?.(selector);
    let node = start;
    while (node && node !== document.documentElement) {
      const top = readElementScrollTop(node);
      if (top > max) max = top;
      node = node.parentElement;
    }
  });
  return max;
}

export function shouldShowPageScrollToTop(scrollTop = getMaxPageScrollTop()) {
  return Number(scrollTop) >= PAGE_SCROLL_TOP_THRESHOLD;
}

function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

let resetGeneration = 0;

/** Cancel a pending repeated reset so a later results-scroll restore can win. */
export function cancelPageScrollReset() {
  resetGeneration += 1;
}

export function resetPageScrollToTop({
  repeat = true,
  smooth = false,
  includeAgentShell = true,
} = {}) {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const generation = ++resetGeneration;
  const behavior = smooth && !prefersReducedMotion() ? "smooth" : "instant";
  const opts = { includeAgentShell };
  const reset = () => {
    if (generation !== resetGeneration) return;
    scrollWindowToTop(behavior);
    resetElementScroll(document.documentElement, behavior);
    resetElementScroll(document.body, behavior);
    scrollSelectors(opts).forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        if (!includeAgentShell && isAgentShellNode(el)) return;
        resetElementScroll(el, behavior);
      });
    });
    const seen = new Set();
    SCROLL_ANCESTOR_START_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((start) => {
        if (seen.has(start)) return;
        seen.add(start);
        resetAncestorScrollports(start, behavior, opts);
      });
    });
  };

  reset();
  if (repeat && behavior !== "smooth") {
    window.requestAnimationFrame(reset);
    window.setTimeout(reset, 120);
  }
}
